import { useVirtualizer } from "@tanstack/react-virtual";
import type { RefObject } from "react";

export const TABLE_ROW_HEIGHT_ESTIMATE = 56;
export const TABLE_ROW_OVERSCAN = 8;

interface UseTableVirtualizerOptions {
  count: number;
  /**
   * Vertical scroll container for row virtualization. The dashboard owns
   * the scroller (the `overflow-y-auto` wrapper around the tab content)
   * and passes its ref down — only the visible window of rows mounts,
   * regardless of how many records a tenant has.
   */
  scrollContainerRef?: RefObject<HTMLElement | null>;
}

/**
 * Row virtualizer + top/bottom spacer math shared by the People, Roles, and
 * Invites tables. Falls back to no-op virtualization when no scroll
 * container ref is provided (consumers that pass a small row list can omit
 * it).
 */
export function useTableVirtualizer({ count, scrollContainerRef }: UseTableVirtualizerOptions) {
  // No `scrollMargin` here: the sticky `<thead>` is part of the table's
  // natural flow, so rows are already pushed below it by the spacer-row
  // pattern. Setting `scrollMargin: headerHeight` introduced a one-row
  // top gap on tab remount because the virtualizer would re-shift its
  // origin while the sticky header occupied the same pixels twice.
  // Overscan absorbs any minor off-by-N row from ignoring the header.
  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    estimateSize: () => TABLE_ROW_HEIGHT_ESTIMATE,
    overscan: TABLE_ROW_OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const totalSize = virtualizer.getTotalSize();
  const topPad = virtualItems[0]?.start ?? 0;
  const bottomPad = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1]!.end : 0;

  return { virtualizer, virtualItems, topPad, bottomPad };
}
